import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Sparkles, Mail, Lock, User, Loader2, Briefcase, 
  Upload, FileText, MapPin, Globe, Phone, ChevronRight, ChevronLeft, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, loginWithGoogle } = useAuth();
  const { t, language } = useLanguage();
  
  const initialRole = searchParams.get('role') || 'client';
  const [role, setRole] = useState(initialRole);
  const [step, setStep] = useState(1);
  
  // Common fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Provider-specific fields
  const [phone, setPhone] = useState('');
  const [serviceCategory, setServiceCategory] = useState('');
  const [bio, setBio] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [hourlyRate, setHourlyRate] = useState('30');
  const [country, setCountry] = useState('DE');
  const [city, setCity] = useState('Hamburg');
  const [languages, setLanguages] = useState(['de']);
  const [verificationFile, setVerificationFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  
  // Config data
  const [categories, setCategories] = useState({});
  const [countries, setCountries] = useState({});
  const [cities, setCities] = useState([]);
  const [availableSpecialties, setAvailableSpecialties] = useState([]);

  useEffect(() => {
    if (role === 'provider') {
      fetch(`${API_URL}/api/config/categories`).then(r => r.json()).then(d => setCategories(d.categories || {}));
      fetch(`${API_URL}/api/config/countries`).then(r => r.json()).then(d => setCountries(d.countries || {}));
    }
  }, [role]);

  useEffect(() => {
    if (country) {
      fetch(`${API_URL}/api/config/cities/${country}`).then(r => r.json()).then(d => {
        setCities(d.cities || []);
        if (d.cities?.length > 0 && !d.cities.includes(city)) setCity(d.cities[0]);
      });
    }
  }, [country]);

  useEffect(() => {
    if (serviceCategory) {
      fetch(`${API_URL}/api/config/specialties/${serviceCategory}`).then(r => r.json()).then(d => {
        setAvailableSpecialties(d.specialties || []);
        setSpecialties([]);
      });
    }
  }, [serviceCategory]);

  const toggleSpecialty = (spec) => {
    setSpecialties(prev => prev.includes(spec) ? prev.filter(s => s !== spec) : [...prev, spec]);
  };

  const toggleLanguage = (lang) => {
    setLanguages(prev => prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PDF, JPEG, PNG ou WebP.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 10MB.');
      return;
    }
    
    setVerificationFile(file);
    setUploadingFile(true);
    
    try {
      // We need a temporary token to upload. First register the user account, then upload.
      // Actually, we'll upload after basic registration. For now just store the file reference.
      setUploadedFileName(file.name);
      toast.success('Document sélectionné');
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleClientSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error(t('auth.passwordMismatch')); return; }
    if (password.length < 6) { toast.error(t('auth.passwordTooShort')); return; }
    
    setLoading(true);
    try {
      // Pass the current interface language to use as user's preferred language
      await register(email, password, name, 'client', language);
      toast.success(t('common.success'));
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSubmit = async () => {
    if (!serviceCategory) { toast.error(t('auth.chooseCategory')); return; }
    
    setLoading(true);
    try {
      // Step 1: Register provider via enhanced endpoint (include interface language)
      const response = await fetch(`${API_URL}/api/auth/register-provider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, password, name, phone,
          service_category: serviceCategory,
          bio, specialties,
          hourly_rate: parseFloat(hourlyRate) || 30,
          country, city,
          languages,
          preferred_language: language, // Use interface language as user's preferred language
          verification_document_path: null,
          verification_document_name: uploadedFileName || null
        })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Registration failed');
      }
      
      const data = await response.json();
      localStorage.setItem('token', data.access_token);
      
      // Step 2: Upload verification document if selected
      if (verificationFile) {
        const formData = new FormData();
        formData.append('file', verificationFile);
        
        const uploadResp = await fetch(`${API_URL}/api/upload/document`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${data.access_token}` },
          body: formData
        });
        
        if (uploadResp.ok) {
          const uploadData = await uploadResp.json();
          // Update the provider's verification document path
          await fetch(`${API_URL}/api/providers/profile`, {
            method: 'PUT',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.access_token}` 
            },
            body: JSON.stringify({})
          });
        }
      }
      
      // Manually set user in auth context by reloading
      toast.success(t('common.success'));
      window.location.href = '/provider/dashboard';
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!name || !email || !password || !confirmPassword) { toast.error(t('auth.fillAllFields') || 'Bitte füllen Sie alle Felder aus'); return; }
      if (password !== confirmPassword) { toast.error(t('auth.passwordMismatch') || 'Passwörter stimmen nicht überein'); return; }
      if (password.length < 6) { toast.error(t('auth.passwordTooShort') || 'Passwort zu kurz (min. 6 Zeichen)'); return; }
    }
    if (step === 2 && !serviceCategory) { toast.error(t('auth.chooseCategory') || 'Bitte wählen Sie eine Kategorie'); return; }
    setStep(s => s + 1);
  };

  const LANG_OPTIONS = [
    { code: 'de', label: 'Deutsch' },
    { code: 'en', label: 'English' },
  ];

  const totalSteps = role === 'provider' ? 3 : 1;

  return (
    <div className="min-h-screen flex items-center justify-center py-20 px-4 bg-background" data-testid="register-page">
      <div className="w-full max-w-lg animate-fade-in-up">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-accent-foreground" />
            </div>
            <span className="font-heading text-2xl font-bold">AFY</span>
          </Link>
          <h1 className="font-heading text-3xl font-semibold mb-2">{t('auth.registerTitle')}</h1>
          <p className="text-muted-foreground">{t('auth.registerSubtitle')}</p>
        </div>

        <Card className="border-border/50 shadow-soft">
          <CardContent className="pt-6">
            {/* Role Tabs */}
            <Tabs value={role} onValueChange={(v) => { setRole(v); setStep(1); }} className="mb-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="client" data-testid="tab-client">
                  <User className="w-4 h-4 mr-2" />
                  {t('auth.client')}
                </TabsTrigger>
                <TabsTrigger value="provider" data-testid="tab-provider">
                  <Briefcase className="w-4 h-4 mr-2" />
                  {t('auth.provider')}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Step indicator for provider */}
            {role === 'provider' && (
              <div className="flex items-center justify-center gap-2 mb-6" data-testid="step-indicator">
                {[1, 2, 3].map(s => (
                  <div key={s} className="flex items-center gap-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      s < step ? 'bg-accent text-accent-foreground' : s === step ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
                    }`}>
                      {s < step ? <Check className="w-4 h-4" /> : s}
                    </div>
                    {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-accent' : 'bg-muted'}`} />}
                  </div>
                ))}
              </div>
            )}

            {/* CLIENT REGISTRATION - Simple form */}
            {role === 'client' && (
              <>
                <Button variant="outline" className="w-full h-12 mb-6" onClick={loginWithGoogle} data-testid="google-register-btn">
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t('auth.continueGoogle')}
                </Button>
                <div className="relative mb-6"><Separator /><span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">{t('auth.orEmail')}</span></div>
                <form onSubmit={handleClientSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('auth.name')}</Label>
                    <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" /><Input type="text" placeholder="John Doe" value={name} onChange={e => setName(e.target.value)} className="pl-10 h-12" required data-testid="name-input" /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auth.email')}</Label>
                    <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" /><Input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 h-12" required data-testid="email-input" /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auth.password')}</Label>
                    <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" /><Input type="password" placeholder={t('auth.minChars')} value={password} onChange={e => setPassword(e.target.value)} className="pl-10 h-12" required minLength={6} data-testid="password-input" /></div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('auth.confirmPassword')}</Label>
                    <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" /><Input type="password" placeholder={t('auth.confirm')} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="pl-10 h-12" required data-testid="confirm-password-input" /></div>
                  </div>
                  <Button type="submit" className="btn-primary w-full h-12" disabled={loading} data-testid="register-submit-btn">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.loading')}</> : t('auth.createAccount')}
                  </Button>
                </form>
              </>
            )}

            {/* PROVIDER REGISTRATION - Multi-step */}
            {role === 'provider' && (
              <div className="space-y-4">
                {/* Step 1: Basic info */}
                {step === 1 && (
                  <div className="space-y-4" data-testid="provider-step-1">
                    <h3 className="font-heading text-lg font-medium">{t('auth.personalInfo')}</h3>
                    <div className="space-y-2">
                      <Label>{t('auth.fullName')}</Label>
                      <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" /><Input placeholder="Max Mustermann" value={name} onChange={e => setName(e.target.value)} className="pl-10 h-12" data-testid="provider-name-input" /></div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('auth.professionalEmail')}</Label>
                      <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" /><Input type="email" placeholder="pro@email.com" value={email} onChange={e => setEmail(e.target.value)} className="pl-10 h-12" data-testid="provider-email-input" /></div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('auth.phone')}</Label>
                      <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" /><Input type="tel" placeholder="+49 123 456 7890" value={phone} onChange={e => setPhone(e.target.value)} className="pl-10 h-12" data-testid="provider-phone-input" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>{t('auth.password')}</Label>
                        <Input type="password" placeholder="Min. 6" value={password} onChange={e => setPassword(e.target.value)} className="h-12" data-testid="provider-password-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('auth.confirmPassword')}</Label>
                        <Input type="password" placeholder={t('auth.confirm')} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="h-12" data-testid="provider-confirm-password-input" />
                      </div>
                    </div>
                    <Button className="btn-primary w-full h-12" onClick={nextStep} data-testid="next-step-btn">
                      {t('auth.next')} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}

                {/* Step 2: Service details + languages */}
                {step === 2 && (
                  <div className="space-y-4" data-testid="provider-step-2">
                    <h3 className="font-heading text-lg font-medium">{t('auth.yourService')}</h3>
                    <div className="space-y-2">
                      <Label>{t('auth.serviceCategory')}</Label>
                      <Select value={serviceCategory} onValueChange={setServiceCategory}>
                        <SelectTrigger className="h-12" data-testid="category-select">
                          <SelectValue placeholder={t('auth.chooseCategory')} />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(categories).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.de || val.en}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('auth.descriptionBio')}</Label>
                      <Textarea placeholder={t('auth.describePlaceholder')} value={bio} onChange={e => setBio(e.target.value)} className="min-h-[100px]" data-testid="bio-textarea" />
                    </div>
                    {availableSpecialties.length > 0 && (
                      <div className="space-y-2">
                        <Label>{t('auth.specialties')}</Label>
                        <div className="flex flex-wrap gap-2">
                          {availableSpecialties.map(spec => (
                            <Badge key={spec} variant={specialties.includes(spec) ? "default" : "outline"} className="cursor-pointer px-3 py-1" onClick={() => toggleSpecialty(spec)} data-testid={`specialty-${spec}`}>
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>{t('auth.hourlyRate')}</Label>
                      <Input type="number" min="5" max="500" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} className="h-12" data-testid="hourly-rate-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('auth.spokenLanguages')}</Label>
                      <div className="flex flex-wrap gap-2">
                        {LANG_OPTIONS.map(({ code, label }) => (
                          <Badge key={code} variant={languages.includes(code) ? "default" : "outline"} className="cursor-pointer px-3 py-1" onClick={() => toggleLanguage(code)} data-testid={`lang-${code}`}>
                            {label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button variant="outline" className="flex-1 h-12" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-1" /> {t('auth.back')}</Button>
                      <Button className="btn-primary flex-1 h-12" onClick={nextStep} data-testid="next-step-btn">{t('auth.next')} <ChevronRight className="w-4 h-4 ml-1" /></Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Verification document */}
                {step === 3 && (
                  <div className="space-y-4" data-testid="provider-step-3">
                    <h3 className="font-heading text-lg font-medium">{t('auth.professionalVerification')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('auth.verificationDescription')}
                    </p>
                    <div className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-accent transition-colors">
                      <input type="file" id="kbis-upload" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFileUpload} data-testid="kbis-file-input" />
                      <label htmlFor="kbis-upload" className="cursor-pointer">
                        {uploadedFileName ? (
                          <div className="flex items-center justify-center gap-3">
                            <FileText className="w-8 h-8 text-accent" />
                            <div className="text-left">
                              <p className="font-medium text-sm">{uploadedFileName}</p>
                              <p className="text-xs text-muted-foreground">{t('auth.clickToChange')}</p>
                            </div>
                            <Check className="w-5 h-5 text-green-500" />
                          </div>
                        ) : (
                          <>
                            <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                            <p className="font-medium text-sm">{t('auth.clickToUpload')}</p>
                            <p className="text-xs text-muted-foreground mt-1">PDF, JPEG, PNG (max. 10MB)</p>
                          </>
                        )}
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t('auth.documentOptional')}
                    </p>
                    <div className="flex gap-3">
                      <Button variant="outline" className="flex-1 h-12" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-1" /> {t('auth.back')}</Button>
                      <Button className="btn-primary flex-1 h-12" onClick={handleProviderSubmit} disabled={loading} data-testid="provider-register-submit-btn">
                        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('auth.registering')}...</> : <>{t('auth.finalize')} <Check className="w-4 h-4 ml-1" /></>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-muted-foreground mt-6">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="text-accent hover:underline font-medium" data-testid="login-link">
            {t('nav.login')}
          </Link>
        </p>
      </div>
    </div>
  );
}
